import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { GanttBar, pct } from '../components/GanttBar'
import { RefreshReportCard } from '../components/RefreshReportCard'
import { StageEditor } from '../components/StageEditor'
import { StatusChip } from '../components/StatusChip'
import { COMMON, REFRESH_VIEW } from '../labels'
import { longDate, shortDate } from '../lib/format'
import type { CycleView, StagePlan } from '../lib/types'
import {
  BAND_SOFT,
  FONT_MONO,
  INK_FAINT,
  INK_SOFT,
  INK,
  LINE,
  RISK_COLOR,
  serifNumber,
} from '../theme'

interface Props {
  cycle: CycleView
  today: string
  /** Recargar tras capturar avance: el plan entero se recalcula en el backend. */
  onChanged: () => void
}

/** Ancho de la columna de etapas; lo comparten las filas y el eje del pie. */
const STAGE_W = 220

/**
 * La ventana completa de trabajo, no sólo el deadline.
 *
 * Cada etapa se dibuja dos veces: la barra sólida es lo PROYECTADO y la marca
 * fina es la ÚLTIMA fecha permitida (planeación hacia atrás desde el deadline).
 * Cuando la barra rebasa la marca, esa etapa ya se está comiendo el buffer, y
 * se ve sin tener que leer un número.
 */
export function RefreshView({ cycle, today, onChanged }: Props) {
  const { window_start, deadline } = cycle
  const riskColor = RISK_COLOR[cycle.risk] ?? '#A6998A'

  return (
    // Una sola tarjeta por ciclo: la ventana y sus etapas son el mismo objeto,
    // y partirlas en dos cajas obligaba a recomponerlas con la vista.
    <Paper sx={{ p: 3 }}>
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="h2">{cycle.cycle_name}</Typography>
            <Typography
              sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 0.4 }}
            >
              {REFRESH_VIEW.window}: {longDate(window_start)} → {longDate(deadline)}
              {cycle.window_weeks ? ` · ${cycle.window_weeks} semanas` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {cycle.is_override && (
              <Tooltip title={cycle.override_note ?? ''}>
                <Chip size="small" label={REFRESH_VIEW.overrideNote} variant="outlined" />
              </Tooltip>
            )}
            <StatusChip value={cycle.risk} kind="risk" />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          sx={{
            mt: 2.5,
            gap: 0,
            rowGap: 2,
            borderTop: `1px solid ${LINE}`,
            pt: 2.5,
          }}
        >
          <Metric
            label={REFRESH_VIEW.bufferRemaining}
            value={`${cycle.buffer_remaining}/${cycle.buffer_days}`}
            color={cycle.buffer_remaining === 0 ? riskColor : undefined}
          />
          <Metric label={REFRESH_VIEW.progress} value={`${Math.round(cycle.progress * 100)}%`} />
          <Metric label={REFRESH_VIEW.projected} value={shortDate(cycle.projected_finish)} />
          {cycle.days_over_deadline > 0 && (
            <Metric
              label={REFRESH_VIEW.overDeadline}
              value={`+${cycle.days_over_deadline}`}
              color={riskColor}
            />
          )}
        </Stack>

        {cycle.config_warning && (
          <Alert severity="error" sx={{ mt: 2.5 }}>
            {cycle.config_warning}
          </Alert>
        )}

        {!cycle.ready_to_start && cycle.missing_sources.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2.5 }}>
            {REFRESH_VIEW.blockedBy}: {cycle.missing_sources.join(', ')}
          </Alert>
        )}
        {cycle.ready_to_start && (
          <Alert severity="success" sx={{ mt: 2.5 }}>
            {REFRESH_VIEW.readyToStart}
          </Alert>
        )}
      </Box>

      <Box sx={{ mt: 3.5, pt: 3, borderTop: `1px solid ${LINE}` }}>
        <Typography variant="h2" sx={{ mb: 2.5 }}>
          {REFRESH_VIEW.stages}
        </Typography>

        <Stack spacing={1.5}>
          {cycle.stages.map((s) => (
            <StageRow
              key={s.id}
              stage={s}
              windowStart={window_start}
              deadline={deadline}
              today={today}
              cycleColor={cycle.color}
              cycleId={cycle.cycle_id}
              period={cycle.data_period}
              onChanged={onChanged}
            />
          ))}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 2, pl: `${STAGE_W}px`, pr: '122px' }}
        >
          <Axis>{longDate(window_start)}</Axis>
          <Axis>{longDate(deadline)}</Axis>
        </Stack>
      </Box>

      <RefreshReportCard cycleId={cycle.cycle_id} period={cycle.data_period} />
    </Paper>
  )
}

function StageRow({
  stage,
  windowStart,
  deadline,
  today,
  cycleColor,
  cycleId,
  period,
  onChanged,
}: {
  stage: StagePlan
  windowStart: string
  deadline: string
  today: string
  cycleColor: string
  cycleId: string
  period: string
  onChanged: () => void
}) {
  const done = Boolean(stage.actual_finish)
  const color = done ? '#3F8F5B' : stage.slack_days < 0 ? '#DF412A' : cycleColor

  const start = stage.actual_start ?? stage.projected_start
  const finish = stage.actual_finish ?? stage.projected_finish

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        borderRadius: 3,
        py: 0.5,
        transition: 'background-color 160ms',
        '&:hover': { bgcolor: BAND_SOFT },
      }}
    >
      <Box sx={{ width: STAGE_W, flexShrink: 0, pl: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: stage.is_gate ? 700 : 500, color: INK }}
          >
            {stage.name}
          </Typography>
          {stage.compute_jobs.length > 0 && (
            <Tooltip
              title={`${REFRESH_VIEW.computeWarning}: ${stage.compute_jobs
                .map((j) => `${j.name} ~${j.hours}h`)
                .join(' · ')}`}
            >
              <WarningAmberIcon sx={{ fontSize: 15, color: '#DE8A1B' }} />
            </Tooltip>
          )}
        </Stack>
        <Axis>
          {stage.days > 0 ? `${stage.days} d · ` : ''}
          {REFRESH_VIEW.slack} {stage.slack_days}
        </Axis>
      </Box>

      <Box sx={{ flex: 1 }}>
        <GanttBar
          height={24}
          segments={
            stage.is_gate
              ? []
              : [
                  {
                    start: pct(start, windowStart, deadline),
                    end: pct(finish, windowStart, deadline),
                    color,
                    label: '',
                    faded: !done && stage.status === 'not_started',
                    tooltip: `${stage.name}\n${REFRESH_VIEW.projected}: ${shortDate(start)} → ${shortDate(finish)}\n${REFRESH_VIEW.latestFinish}: ${shortDate(stage.latest_finish)}`,
                  },
                ]
          }
          markers={[
            {
              at: pct(stage.latest_finish, windowStart, deadline),
              color: INK_FAINT,
              label: `${REFRESH_VIEW.latestFinish}: ${longDate(stage.latest_finish)}`,
            },
            {
              at: pct(today, windowStart, deadline),
              color: INK,
              label: COMMON.today,
              solid: true,
            },
          ]}
        />
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ width: 122, flexShrink: 0, pr: 0.5 }}
      >
        <StatusChip value={stage.status} kind="stage" />
        <StageEditor
          cycleId={cycleId}
          period={period}
          today={today}
          stage={stage}
          onSaved={onChanged}
        />
      </Stack>
    </Stack>
  )
}

/**
 * Marcador: etiqueta mono diminuta arriba, cifra serif grande abajo.
 *
 * El salto de tamaño (10 px → 34 px) y el cambio de tipografía hacen el trabajo
 * que en otros tableros hace una caja con borde. Menos cromo, más jerarquía.
 */
function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ pr: 6 }}>
      <Box
        component="span"
        sx={{
          display: 'block',
          fontFamily: FONT_MONO,
          fontSize: '0.625rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: INK_FAINT,
          mb: 0.75,
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ ...serifNumber, fontSize: '2.125rem', color: color ?? INK }}>
        {value}
      </Box>
    </Box>
  )
}

function Axis({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        fontFamily: FONT_MONO,
        fontSize: '0.6875rem',
        color: INK_SOFT,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </Box>
  )
}
